import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Modal, Form, Button, TextArea, Icon, Loader, Dimmer } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { useForm, Controller } from 'react-hook-form'
import { ActionPlanService } from 'utility/services/action-plan'
import { BaseService } from 'utility/services/base'
import { APIPath, DatePickerTypes } from 'utility/constant'
import { FormDateField } from 'elements'
import styled from 'styled-components'
import { useAuth } from 'context'
import { InspectionService } from 'utility/services/inspection'
import Webcam from 'react-webcam'
import { X } from 'phosphor-react'
import { FlexRowBetween, TitleText } from 'components'
import { useNotification } from 'hooks'
import { TicketFileTile } from 'components/TicketFileTile'
import { isImageAttachment } from 'utility/ticket-attachment'

const StyledModal = styled(Modal)`
    .content {
        padding: 2rem !important;
    }
    textarea {
        min-height: 100px;
    }
    .media-buttons {
        display: flex;
        gap: 10px;
        margin-bottom: 15px;
    }
    .preview-container {
        margin-top: 10px;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        gap: 15px;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
    }
    .preview-box {
        position: relative;
        width: 100%;
        padding-bottom: 100%;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    .preview-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    .delete-icon {
        position: absolute;
        top: 5px;
        right: 5px;
        background: rgba(0, 0, 0, 0.6);
        border-radius: 50%;
        padding: 4px;
        cursor: pointer;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        &:hover {
            background: rgba(220, 53, 69, 0.9);
        }
    }
    .image-loader {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f8f9fa;
    }
    .camera-container {
        margin-top: 15px;
        background: #f8f9fa;
        padding: 15px;
        border-radius: 8px;
        video {
            width: 100%;
            max-width: 400px;
            border-radius: 4px;
            margin-bottom: 10px;
        }
        canvas {
            display: none;
        }
        .camera-buttons {
            display: flex;
            gap: 10px;
            margin-top: 10px;
        }
    }
`

// Helper function to format the answer for display
const formatAnswerDisplay = answer => {
    if (!answer) return 'N/A'

    if (answer.TextResponse) {
        return answer.TextResponse
    }

    if (answer.DateResponse) {
        // Format date as needed
        return new Date(answer.DateResponse).toLocaleDateString()
    }

    if (answer.SingleResponse) {
        return answer.SingleResponse.DisplayText || 'N/A'
    }

    if (answer.MultipleResponse && answer.MultipleResponse.length > 0) {
        const selected = answer.MultipleResponse.filter(opt => opt.IsSelected)
        if (selected.length > 0) {
            return selected.map(opt => opt.DisplayText).join(', ')
        }
    }

    // Handle ParentChildResponse if necessary
    if (answer.ParentChildResponse && answer.ParentChildResponse.length > 0) {
        const parts = answer.ParentChildResponse.map(parent => {
            const selectedChildren = (parent.Children || []).filter(
                c => c.IsSelected,
            )
            if (selectedChildren.length > 0) {
                const childNames = selectedChildren
                    .map(c => c.ChildName)
                    .filter(Boolean)
                    .join(', ')
                return `${parent.ParentName} -> ${childNames || 'N/A'}`
            }
            if (parent.IsSelected) {
                return parent.ParentName
            }
            return null
        }).filter(Boolean)

        if (parts.length > 0) {
            return parts.join('; ')
        }
    }

    // Fallback for other types or empty answers
    return 'N/A' // Or JSON.stringify(answer) if preferred as fallback
}

const RaiseTicketModal = ({ open, onClose, question, answer, auditId }) => {
    const [teamMembers, setTeamMembers] = useState([])
    const [nonTeamOptions, setNonTeamOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const { user } = useAuth()
    const [showCamera, setShowCamera] = useState(false)
    const [mediaFiles, setMediaFiles] = useState([])
    const [loadingImages, setLoadingImages] = useState({})
    const [isUploadingMedia, setIsUploadingMedia] = useState(false)
    const [deletingMediaId, setDeletingMediaId] = useState(null)
    const webcamRef = useRef(null)
    const { successToast, errorToast, warningToast } = useNotification()

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            description: '',
            selectedMember: [],
            nonTeamMembers: [],
            DueDate: null,
        },
    })

    useEffect(() => {
        if (open) {
            fetchTeamMembers()
            reset({ description: '', selectedMember: [], nonTeamMembers: [], DueDate: null })
            setMediaFiles([])
        } else {
            reset()
            stopCamera()
        }
    }, [open, reset])

    const fetchTeamMembers = async () => {
        try {
            const response = await BaseService.get(`${APIPath.teamMemberByAudit}${auditId}`)
            const options = response.map(member => ({
                key: member.unique_id,
                text: member.Name,
                value: member.unique_id,
            }))
            setTeamMembers(options)
        } catch (error) {
            console.error('Error fetching team members:', error)
        }
    }

    const startCamera = () => {
        setShowCamera(true)
    }

    const stopCamera = () => {
        setShowCamera(false)
    }

    const handleMediaUpload = useCallback(async files => {
        if (!files?.length) return
        setIsUploadingMedia(true)
        try {
            const uploadedMediaFiles = await Promise.all(
                files.map(async file => {
                    const uploadResponse = await InspectionService.uploadMedia({
                        media: file,
                    })

                    const savedMedia = await InspectionService.saveMedia([
                        {
                            MediaId: 0,
                            FAQId: question.Id,
                            Order: 1,
                            FolderName: '1',
                            FileType: file.type || 'application/octet-stream',
                            FileName: (file.name || '').slice(0, 200),
                            SignedURL:
                                uploadResponse.ImagePathUrl ||
                                uploadResponse.FileURL,
                            FileURL:
                                uploadResponse.ImagePathUrl ||
                                uploadResponse.FileURL,
                            FileKey:
                                uploadResponse.CloudImageId ||
                                uploadResponse.FileKey,
                            MediaSourceType: 2,
                        },
                    ])

                    // keep the type / name for the preview (the saved row may not echo them)
                    const saved = savedMedia[0] || {}
                    const mediaFile = {
                        ...saved,
                        FileType: saved.FileType || file.type || 'application/octet-stream',
                        FileName: saved.FileName || file.name || '',
                    }
                    // Set loading state for a new image (other files show a file tile, nothing to load)
                    if (isImageAttachment(mediaFile)) {
                        setLoadingImages(prev => ({
                            ...prev,
                            [mediaFile.MediaId]: true,
                        }))
                    }

                    return mediaFile
                }),
            )

            setMediaFiles(prev => [...prev, ...uploadedMediaFiles])
        } catch (error) {
            console.error('Error uploading media:', error)
            errorToast('The file could not be uploaded. Please try again.')
        } finally {
            setIsUploadingMedia(false)
        }
    }, [question.Id])

    const captureImage = useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot()
        if (imageSrc) {
            fetch(imageSrc)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], `capture-${Date.now()}.jpg`, {
                        type: 'image/jpeg',
                    })
                    handleMediaUpload([file])
                })
                .catch(err => console.error('Error processing image capture:', err))
        }
        setShowCamera(false)
    }, [handleMediaUpload])

    const handleImageLoad = mediaId => {
        if (mediaId) {
            setLoadingImages(prev => ({
                ...prev,
                [mediaId]: false,
            }))
        }
    }

    const handleImageError = mediaId => {
        if (mediaId) {
            setLoadingImages(prev => ({
                ...prev,
                [mediaId]: false,
            }))
        }
    }

    const handleDeleteMedia = async (mediaId, index) => {
        if (!mediaId) {
            // If no mediaId, just remove from local state (for newly uploaded files not yet saved)
            setMediaFiles(prev => prev.filter((_, i) => i !== index))
            return
        }

        try {
            setDeletingMediaId(mediaId)
            await InspectionService.deleteMedia(mediaId)
            setMediaFiles(prev => prev.filter(file => file.MediaId !== mediaId))
            setLoadingImages(prev => {
                const newState = { ...prev }
                delete newState[mediaId]
                return newState
            })
        } catch (error) {
            console.error('Error deleting media:', error)
        } finally {
            setDeletingMediaId(null)
        }
    }

    const onSubmit = async formData => {
        if (
            !formData.selectedMember ||
            formData.selectedMember.length === 0 ||
            !formData.DueDate ||
            !formData.description
        ) {
            console.warn('Missing required fields')
            warningToast('Please fill all required fields before submitting the ticket.')
            return
        }

        setLoading(true)

        let selectedAnswerId = null
        const answers = question?.Answers

        if (answers?.SingleResponse?.Id) {
            selectedAnswerId = answers.SingleResponse.Id
        } else if (answers?.MultipleResponse?.length > 0) {
            const selectedOption = answers.MultipleResponse.find(
                opt => opt.IsSelected,
            )
            if (selectedOption) {
                selectedAnswerId = selectedOption.Id
            }
        }

        try {
            const data = {
                AuditId: auditId,
                AuditQuestionId: question.Id,
                AuditAnswerId: selectedAnswerId,
                CreatedByUserId: user?.unique_id,
                AssignedToUserId: Array.isArray(formData.selectedMember) && formData.selectedMember.length > 0 ? formData.selectedMember[0] : null,
                AssignedToUserIds: Array.isArray(formData.selectedMember) && formData.selectedMember.length > 0 ? formData.selectedMember.join(';') : '',
                AssignedToNonUserId: Array.isArray(formData.nonTeamMembers) && formData.nonTeamMembers.length > 0 ? formData.nonTeamMembers.join(';') : '',
                TicketStatus: 1,
                DueDate: formData.DueDate.toISOString(),
                Description: formData.description,
            }

            // Step 1: Create action plan and get the ID
            const actionPlanId = await ActionPlanService.create(data)

            // Step 2: If there are media files, update their references
            if (mediaFiles.length > 0) {
                const mediaIds = mediaFiles.map(file => file.MediaId)
                await ActionPlanService.updateMediaReferences(mediaIds, actionPlanId)
            }

            successToast('Support ticket submitted successfully.')
            onClose(true)
        } catch (error) {
            console.error('Error creating action plan:', error)
            errorToast('Failed to submit support ticket. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const displayedAnswer = formatAnswerDisplay(answer)

    return (
        <StyledModal open={open} onClose={() => onClose(false)} size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>Raise Ticket</TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose(false)}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form onSubmit={e => e.preventDefault()}>
                    <Form.Field>
                        <label>Question</label>
                        <div
                            dangerouslySetInnerHTML={{ __html: question.FAQ }}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Answer</label>
                        <div>{displayedAnswer}</div>
                    </Form.Field>
                    <Form.Field error={!!errors.description}>
                        <label>Description</label>
                        <Controller
                            name="description"
                            control={control}
                            rules={{ required: 'Description is required' }}
                            render={({ field }) => (
                                <TextArea
                                    placeholder="Enter description for the action plan..."
                                    {...field}
                                />
                            )}
                        />
                        {errors.description && (
                            <span style={{ color: 'red' }}>
                                {errors.description.message}
                            </span>
                        )}
                    </Form.Field>
                    <Form.Field error={!!errors.selectedMember}>
                        <label>Team Member</label>
                        <Controller
                            name="selectedMember"
                            control={control}
                            rules={{ required: 'Team Member is required' }}
                            render={({ field: { onChange, value } }) => (
                                <Dropdown
                                    placeholder="Select Team Member(s)"
                                    fluid
                                    multiple
                                    selection
                                    options={teamMembers}
                                    onChange={(_, data) => onChange(data.value)}
                                    value={value}
                                    error={!!errors.selectedMember}
                                />
                            )}
                        />
                        {errors.selectedMember && (
                            <span style={{ color: 'red' }}>
                                {errors.selectedMember.message}
                            </span>
                        )}
                    </Form.Field>
                    <Form.Field>
                        <label>Non-Team Member</label>
                        <Controller
                            name="nonTeamMembers"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Dropdown
                                    placeholder="Type emails and press Enter"
                                    fluid
                                    multiple
                                    selection
                                    allowAdditions
                                    search
                                    noResultsMessage="Please Type User Email id"
                                    options={nonTeamOptions}
                                    value={value}
                                    onAddItem={(_, { value: v }) => {
                                        const option = { key: v, text: v, value: v }
                                        setNonTeamOptions(prev =>
                                            prev.some(o => o.value === v)
                                                ? prev
                                                : [...prev, option],
                                        )
                                    }}
                                    onChange={(_, data) => onChange(data.value)}
                                />
                            )}
                        />
                    </Form.Field>
                    <FormDateField
                        name="DueDate"
                        control={control}
                        label="Due Date"
                        errors={errors.DueDate}
                        type={DatePickerTypes.Single}
                        placeholder="Select due date"
                        rules={{ required: 'Due Date is required' }}
                    />
                    <Form.Field>
                        <label>Media</label>
                        <div className="media-buttons">
                            <Button
                                type="button"
                                icon
                                labelPosition="left"
                                onClick={() =>
                                    document
                                        .getElementById('file-input')
                                        .click()
                                }
                                primary
                            >
                                <Icon name="upload" />
                                Upload File
                            </Button>
                            <Button
                                type="button"
                                icon
                                labelPosition="left"
                                onClick={startCamera}
                                secondary
                            >
                                <Icon name="camera" />
                                Take Picture
                            </Button>
                            <input
                                id="file-input"
                                type="file"
                                multiple
                                style={{ display: 'none' }}
                                onChange={e =>
                                    handleMediaUpload(
                                        Array.from(e.target.files),
                                    )
                                }
                            />
                        </div>

                        {(isUploadingMedia || mediaFiles.length > 0) && (
                            <div className="preview-container">
                                {isUploadingMedia && (
                                    <div className="preview-box">
                                        <Dimmer active inverted>
                                            <Loader />
                                        </Dimmer>
                                    </div>
                                )}
                                {mediaFiles.map((file, index) => {
                                    const isImage = isImageAttachment(file)
                                    const isLoading = isImage && file.MediaId ? loadingImages[file.MediaId] : false
                                    return (
                                        <div key={file.MediaId || `temp-${index}`} className="preview-box">
                                            {isLoading && (
                                                <Dimmer active inverted>
                                                    <Loader />
                                                </Dimmer>
                                            )}
                                            {isImage ? (
                                                <img
                                                    src={file.FileURL}
                                                    alt={`Preview ${index + 1}`}
                                                    className="preview-image"
                                                    onLoad={() => handleImageLoad(file.MediaId)}
                                                    onError={() => handleImageError(file.MediaId)}
                                                    style={{
                                                        display: isLoading ? 'none' : 'block',
                                                    }}
                                                />
                                            ) : (
                                                <TicketFileTile
                                                    url={file.FileURL || file.SignedURL}
                                                    file={file}
                                                    size={100}
                                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                                                />
                                            )}
                                            <div
                                                className="delete-icon"
                                                onClick={() => handleDeleteMedia(file.MediaId, index)}
                                                title="Delete file"
                                                style={{
                                                    opacity: deletingMediaId === file.MediaId ? 0.6 : 1,
                                                    cursor: deletingMediaId === file.MediaId ? 'wait' : 'pointer',
                                                }}
                                            >
                                                {deletingMediaId === file.MediaId ? (
                                                    <Loader active inline size="small" />
                                                ) : (
                                                    <X
                                                        size={18}
                                                        color="white"
                                                        weight="bold"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={() => onClose(false)}>
                    Cancel
                </Button>
                <Button
                    type="button"
                    primary
                    loading={loading}
                    onClick={handleSubmit(onSubmit)}
                >
                    Submit
                </Button>
            </Modal.Actions>

            {/* Camera Modal */}
            <Modal
                open={showCamera}
                onClose={stopCamera}
                size="small"
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <TitleText>Capture Image</TitleText>
                        <X
                            className="pointer"
                            size={32}
                            onClick={stopCamera}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        style={{
                            width: '100%',
                            height: 'auto',
                            maxWidth: '100vw',
                        }}
                        className="responsive-webcam"
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={stopCamera}>
                        Cancel
                    </Button>
                    <Button color="green" onClick={captureImage}>
                        Capture
                    </Button>
                </Modal.Actions>
            </Modal>
        </StyledModal>
    )
}

export default RaiseTicketModal
